import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  fetchAllCatalogItems,
  fetchCatalogItem,
  createCatalogItem,
  updateCatalogItem,
  deleteCatalogItem,
  fetchCatalogProvenance,
  type CatalogItemInsert,
} from '@/lib/api/admin-catalog'
import type { CatalogItem } from '@hangarmax/types'

export const catalogKeys = {
  all: ['admin-catalog'] as const,
  list: () => [...catalogKeys.all, 'list'] as const,
  detail: (id: string) => [...catalogKeys.all, 'detail', id] as const,
}

export function useAdminCatalogList() {
  return useQuery({
    queryKey: catalogKeys.list(),
    queryFn: fetchAllCatalogItems,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCatalogProvenance() {
  return useQuery({
    queryKey: [...catalogKeys.all, 'provenance'] as const,
    queryFn: fetchCatalogProvenance,
    staleTime: 60 * 60 * 1000, // research moves slowly
  })
}

export function useAdminCatalogItem(id: string | undefined) {
  return useQuery({
    queryKey: catalogKeys.detail(id ?? ''),
    queryFn: () => fetchCatalogItem(id!),
    enabled: !!id,
  })
}

export function useCreateCatalogItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (item: CatalogItemInsert) => createCatalogItem(item),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.list() })
      toast.success('Catalog item created')
    },
    onError: (err) => toast.error(`Failed to create: ${err.message}`),
  })
}

export function useUpdateCatalogItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; updates: Partial<CatalogItem> }) =>
      updateCatalogItem(vars.id, vars.updates),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: catalogKeys.list() })
      qc.invalidateQueries({ queryKey: catalogKeys.detail(vars.id) })
      toast.success('Catalog item updated')
    },
    onError: (err) => toast.error(`Failed to update: ${err.message}`),
  })
}

export function useDeleteCatalogItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteCatalogItem(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.list() })
      toast.success('Catalog item deleted')
    },
    onError: (err) => toast.error(`Failed to delete: ${err.message}`),
  })
}
